import { useLingui } from '@lingui/react/macro';
import { Pencil } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';

import { DescriptionList, type DescriptionItem } from '@/shared/record';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';

import type { FlowLeadingStep } from './define-flow';
import { useFlowTrail, type FlowTrailStep } from './flow-trail';
import type { FlowModel } from './use-flow';

export type FlowSummaryVariant = 'panel' | 'strip' | 'review';

export interface FlowSummaryViewProps {
  readonly steps: readonly FlowTrailStep[];
  /**
   * `panel`: o painel "Suas escolhas" (lateral na web, sheet no iPhone); `strip`: a faixa de chips entre 768 e
   * 1280 px; `review`: as mesmas linhas no Resumo, com "Alterar".
   */
  readonly variant?: FlowSummaryVariant;
  /** Aviso no topo do painel ("Trocar o produto limpou as escolhas seguintes"). */
  readonly note?: ReactNode;
  /** Sem o título próprio (o sheet já tem). */
  readonly heading?: boolean;
  /** Antes de ir à etapa (o sheet do iPhone fecha). */
  readonly onBeforeSelect?: () => void;
  readonly className?: string;
}

function Values({ items }: { readonly items: readonly DescriptionItem[] }) {
  const [only] = items;
  if (items.length === 1 && only) return <p className="text-sm font-medium break-words text-foreground">{only.value}</p>;
  return <DescriptionList compact items={items} />;
}

/** "Suas escolhas" sobre um trilho já montado (`useFlowTrail` ou a tela de escolha fora do RHF). */
export function FlowSummaryView({ steps, variant = 'panel', note, heading = true, onBeforeSelect, className }: FlowSummaryViewProps) {
  const { t } = useLingui();
  const headingId = useId();
  const choices = steps.filter((step) => !step.review);
  const done = choices.filter((step) => step.state !== 'pending' && step.items.length > 0);
  const select = (step: FlowTrailStep) =>
    step.onSelect
      ? () => {
          onBeforeSelect?.();
          step.onSelect?.();
        }
      : undefined;

  if (variant === 'strip') {
    if (done.length === 0) return null;
    return (
      <div role="group" aria-label={t`Suas escolhas`} data-slot="flow-summary" data-variant="strip" className={cn('flex flex-wrap gap-1.5', className)}>
        {done.map((step) => {
          const onClick = select(step);
          const value = step.items.map((item) => item.value);
          const content = (
            <>
              <span className="text-muted-foreground">{step.title}</span>
              <span className="truncate font-medium">{value.length === 1 ? value[0] : value.map((part, index) => <span key={index}>{index > 0 ? ' · ' : null}{part}</span>)}</span>
            </>
          );
          const chip = 'inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-foreground';
          return onClick ? (
            <button key={step.id} type="button" onClick={onClick} aria-label={t`Alterar ${step.title}`} className={cn(chip, 'hover:border-input')}>
              {content}
            </button>
          ) : (
            <span key={step.id} className={chip}>
              {content}
            </span>
          );
        })}
      </div>
    );
  }

  if (variant === 'review')
    return (
      // Grupo, não região: no Resumo o painel "Suas escolhas" também está na tela (landmark-unique).
      <div role="group" aria-label={t`Suas escolhas`} data-slot="flow-summary" data-variant="review" className={cn('rounded-card border border-border bg-card', className)}>
        <dl className="divide-y divide-border">
          {choices.map((step) => {
            const onClick = select(step);
            return (
              <div key={step.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 px-4 py-3 md:grid-cols-[10rem_minmax(0,1fr)_auto]">
                <dt className="col-span-2 text-sm text-muted-foreground md:col-span-1">{step.title}</dt>
                <dd className="min-w-0">{step.items.length > 0 ? <Values items={step.items} /> : <p className="text-sm text-muted-foreground">{t`a definir`}</p>}</dd>
                <dd>
                  {onClick ? (
                    <Button type="button" variant="ghost" size="sm" aria-label={t`Alterar ${step.title}`} onClick={onClick}>
                      <Pencil aria-hidden />
                      {t`Alterar`}
                    </Button>
                  ) : null}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    );

  return (
    <section aria-labelledby={heading ? headingId : undefined} aria-label={heading ? undefined : t`Suas escolhas`} data-slot="flow-summary" data-variant="panel" className={cn('flex flex-col gap-3', className)}>
      {heading ? (
        <div className="flex items-baseline justify-between gap-2">
          <h2 id={headingId} className="text-sm font-semibold text-foreground">
            {t`Suas escolhas`}
          </h2>
          <span className="text-xs text-muted-foreground tabular-nums">{t`${done.length} de ${choices.length}`}</span>
        </div>
      ) : null}
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
      <ol className="flex flex-col">
        {choices.map((step) => {
          const onClick = step.state === 'current' ? undefined : select(step);
          return (
            <li key={step.id} className="flex flex-col gap-0.5 border-t border-border py-2.5 first:border-t-0 first:pt-0">
              <div className="flex min-h-7 items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">{step.title}</span>
                {onClick ? (
                  <Button type="button" variant="link" size="sm" aria-label={t`Alterar ${step.title}`} onClick={onClick}>
                    {t`Alterar`}
                  </Button>
                ) : null}
              </div>
              {step.items.length > 0 ? (
                <Values items={step.items} />
              ) : (
                <p className="text-sm text-muted-foreground">{step.state === 'current' ? t`escolhendo agora` : t`a definir`}</p>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export interface FlowSummaryProps<V extends FieldValues> extends Omit<FlowSummaryViewProps, 'steps'> {
  readonly flow: FlowModel<V>;
  /** Etapas anteriores ao fluxo (produto, modalidade). */
  readonly leading?: readonly FlowLeadingStep[];
}

/**
 * "Suas escolhas" do fluxo (U53): lê os valores com `useWatch` e o `summary` de cada etapa concluída; "Alterar" vai à
 * etapa pelo `goTo` (só as alcançáveis, `canGoTo`); as pendentes ficam "a definir". O Resumo usa `variant="review"`.
 */
export function FlowSummary<V extends FieldValues>({ flow, leading, ...props }: FlowSummaryProps<V>) {
  const steps = useFlowTrail(flow, leading);
  return <FlowSummaryView steps={steps} {...props} />;
}
